import type { ChangeEvent, ReactNode } from 'react'
import { useEffect, useMemo } from 'react'

import { Dropdown, Input, Space, Tooltip, type MenuProps } from 'antd'
import { ReloadOutlined, SearchOutlined, DownOutlined } from '@ant-design/icons'

export interface RefreshOption {
  label: string
  value: number
}

export interface PageSearchToolbarProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  onRefresh?: () => void
  refreshInterval?: number | null
  onRefreshIntervalChange?: (value: number | null) => void
  refreshOptions?: RefreshOption[]
  refreshTooltip?: ReactNode
  extraActions?: ReactNode
  className?: string
  inputClassName?: string
  rightSectionClassName?: string
}

const defaultRefreshOptions: RefreshOption[] = [
  { label: 'Every 30 seconds', value: 30_000 },
  { label: 'Every minute', value: 60_000 },
  { label: 'Every 5 minutes', value: 300_000 },
  { label: 'Every 30 minutes', value: 1_800_000 }
]

function formatIntervalLabel(option?: RefreshOption): string {
  if (!option) {
    return 'Manual'
  }

  return option.label
}

export function PageSearchToolbar({
  value,
  onChange,
  placeholder = 'Search…',
  onRefresh,
  refreshInterval = null,
  onRefreshIntervalChange,
  refreshOptions = defaultRefreshOptions,
  refreshTooltip,
  extraActions,
  className,
  inputClassName,
  rightSectionClassName
}: PageSearchToolbarProps): JSX.Element {
  const selectedOption = useMemo(() => {
    if (refreshInterval == null) {
      return undefined
    }

    return refreshOptions.find(option => option.value === refreshInterval)
  }, [refreshInterval, refreshOptions])

  useEffect(() => {
    if (!onRefresh || !refreshInterval) {
      return
    }

    const timer = window.setInterval(() => {
      onRefresh()
    }, refreshInterval)

    return () => {
      window.clearInterval(timer)
    }
  }, [onRefresh, refreshInterval])

  const menuItems: MenuProps['items'] = [
    ...refreshOptions.map(option => ({
      key: option.value.toString(),
      label: option.label,
      onClick: () => onRefreshIntervalChange?.(option.value)
    })),
    { type: 'divider' },
    {
      key: 'manual',
      label: 'Manual refresh',
      onClick: () => onRefreshIntervalChange?.(null)
    }
  ]

  const intervalLabel = formatIntervalLabel(selectedOption)

  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange(event.target.value)
  }

  return (
    <Space className={className} direction='horizontal' size='middle' style={{ width: '100%' }}>
      <Input
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        prefix={<SearchOutlined className='text-slate-400' />}
        allowClear
        className={inputClassName}
      />
      <Space className={rightSectionClassName} size='small'>
        <Tooltip title={refreshTooltip ?? intervalLabel} placement='bottom'>
          <Dropdown.Button menu={{ items: menuItems }} onClick={onRefresh} icon={<DownOutlined />}>
            <Space size={4}>
              <ReloadOutlined />
              <span>{selectedOption ? 'Auto refresh' : 'Refresh'}</span>
            </Space>
          </Dropdown.Button>
        </Tooltip>
        {extraActions}
      </Space>
    </Space>
  )
}
